import { useCallback } from 'react'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { uiActions } from './actions'
import { selectToasts } from './selectors'
import { Toast } from './types'

/**
 * Hook para usar funcionalidades de Toast com Redux
 * Mantém compatibilidade com a interface antiga do ToastContext
 */
export const useToast = () => {
  const dispatch = useAppDispatch()
  const toasts = useAppSelector(selectToasts)

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9)
    const newToast: Toast = {
      ...toast,
      id,
      duration: toast.duration || 4000
    }

    dispatch(uiActions.addToast(newToast))

    // Auto remove toast after duration
    setTimeout(() => {
      dispatch(uiActions.removeToast(id))
    }, newToast.duration)
  }, [dispatch])

  const removeToast = useCallback((id: string) => {
    dispatch(uiActions.removeToast(id))
  }, [dispatch])

  const clearToasts = useCallback(() => {
    dispatch(uiActions.clearToasts())
  }, [dispatch])

  const showSuccess = useCallback((title: string, message?: string, duration?: number) => {
    const id = Math.random().toString(36).substr(2, 9)
    const toastDuration = duration || 4000
    
    dispatch(uiActions.addToast({
      id,
      type: 'success',
      title,
      message,
      duration: toastDuration
    }))

    setTimeout(() => {
      dispatch(uiActions.removeToast(id))
    }, toastDuration)
  }, [dispatch])

  const showError = useCallback((title: string, message?: string, duration?: number) => {
    const id = Math.random().toString(36).substr(2, 9)
    const toastDuration = duration || 4000
    
    dispatch(uiActions.addToast({
      id,
      type: 'error',
      title,
      message,
      duration: toastDuration
    }))

    setTimeout(() => {
      dispatch(uiActions.removeToast(id))
    }, toastDuration)
  }, [dispatch])

  const showWarning = useCallback((title: string, message?: string, duration?: number) => {
    const id = Math.random().toString(36).substr(2, 9)
    const toastDuration = duration || 4000
    
    dispatch(uiActions.addToast({
      id,
      type: 'warning',
      title,
      message,
      duration: toastDuration
    }))

    setTimeout(() => {
      dispatch(uiActions.removeToast(id))
    }, toastDuration)
  }, [dispatch])

  const showInfo = useCallback((title: string, message?: string, duration?: number) => {
    const id = Math.random().toString(36).substr(2, 9)
    const toastDuration = duration || 4000
    
    dispatch(uiActions.addToast({
      id,
      type: 'info',
      title,
      message,
      duration: toastDuration
    }))

    setTimeout(() => {
      dispatch(uiActions.removeToast(id))
    }, toastDuration)
  }, [dispatch])

  return {
    state: { toasts },
    addToast,
    removeToast,
    clearToasts,
    showSuccess,
    showError,
    showWarning,
    showInfo
  }
}

